import React, { useState, useEffect, useRef } from 'react';
import { Popover, Skeleton, Modal, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import CloseIcon from '@material-ui/icons/Close';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import { Link } from 'react-router-dom';
import { isMobile } from '../../helpers/utils';
import theme from '../../shared/theme';
import Avatar, { AVATAR_TYPE } from '../Avatar';
import {
  HoverCardContainer,
  StyledUserName,
  Header,
  BioContainer,
  BioLine,
  BioLinesContainer,
  BioBackground,
  Descriptions,
  DescriptionLine,
  DescriptionLineContent,
  ActiveGroups,
  ReportTooltipTitle,
} from './style';
import {
  StyledButtonOutlined,
  StyledButtonFilled,
  StyledButtonDisabled,
} from '../Button';
import DataProvider from '../../shared/rest';
import './index.less';
import { Roles_Display } from '../../utils';
import ReportModalContent from '../ReportModalContent';
import { useFeatureFlag } from '@crimson-education/feature-switches-web';
import NewPopOverlay from './PopOverlay';
import { getDisplayValue } from '../../views/UserProfile/index.util';

const isApp = isMobile();
const bioIconStyle = {
  size: '18px',
  color: '73747d',
  marginRight: '12px',
};
/**
 * @param {string} name
 * @param {string} lastName
 * @param {string} firstName
 * @returns {string} fullname
 */
export const getFullName = ({ name, lastName, firstName }) => {
  return name ? name : `${firstName} ${lastName}`;
};

export const getReportProps = ({ member, currentUser }) => {
  return {
    relativeId: member.id,
    type: 'member',
    link: `/users/${member.user.id}`,
    emailContent: {
      member: {
        name: getFullName(member.user),
        email: member.user.email,
        id: member.user.id,
      },
      informerName: getFullName(currentUser),
      informerEmail: currentUser.email,
    },
  };
};

// case 'SEND':REQUEST SENT
// case 'IGNORE':REQUEST SENT
// case 'TODO':ACCEPT FRIEND REQUEST
// case 'OK':MESSAGE
// case 'BLOCK': ADD FRIEND

export const ALL_STATUS = {
  REQUEST_SENT: 'REQUEST SENT',
  ADD_FRIEND: 'ADD FRIEND',
  REQUEST_ACCEPTED: 'MESSAGE',
  ACCEPT: 'ACCEPT FRIEND REQUEST',
};

export const RELATION_STATUS = {
  SEND: ALL_STATUS.REQUEST_SENT,
  OK: ALL_STATUS.REQUEST_ACCEPTED,
  IGNORE: ALL_STATUS.REQUEST_SENT,
  BLOCK: ALL_STATUS.ADD_FRIEND,
  TODO: ALL_STATUS.ACCEPT,
};

export const onAcceptOrAddClick = async ({
  relationStatus,
  buttonStatus,
  relationId,
  userId,
  friendId,
  callback,
}) => {
  if (relationStatus === 'BLOCK') {
    return;
  }
  const requestFunc =
    buttonStatus === ALL_STATUS.ADD_FRIEND
      ? 'sendRequest'
      : buttonStatus === ALL_STATUS.ACCEPT
      ? 'acceptRequest'
      : '';
  const requestParams =
    buttonStatus === ALL_STATUS.ADD_FRIEND
      ? { userId, friendId }
      : buttonStatus === ALL_STATUS.ACCEPT
      ? { id: relationId }
      : '';
  if (requestFunc) {
    const { code } = await DataProvider.invoke(
      'user',
      requestFunc,
      requestParams
    );
    if (code === 0) {
      callback();
    }
  }
};

export const onSendMessage = async ({ relation, setRelation }) => {
  const messageLink =
    relation.readyThread === true ? `/messages/${relation.sessionId}` : '';
  if (messageLink) {
    window.xprops.onMessage({
      event: 'open',
      data: messageLink,
    });
  } else {
    const { data: sessionId } = await DataProvider.invoke(
      'user',
      'newSession',
      relation
    );
    setRelation({ ...relation, readyThread: true, sessionId });
    window.xprops.onMessage({
      event: 'open',
      data: `/messages/${sessionId}`,
    });
  }
};

const defaultBio = {
  role: '',
  description: '',
  city: '',
  country: '',
  specialties: '',
  university: '',
  activeGroups: [],
};

const PopOverlay = props => {
  const {
    prefixCls,
    avatarSrc,
    avatarName,
    userName = '',
    userId,
    friendId,
    bio,
    reportProps,
    canViewCGA,
    isExpert,
  } = props;
  const {
    role,
    city,
    country,
    university,
    specialties,
    description,
    pronoun,
  } = {
    ...defaultBio,
    ...bio,
  };
  const NEW_COMMUNITY_PROFILE = useFeatureFlag('NEW_COMMUNITY_PROFILE');
  const specialtiesList = (specialties || '').split(',');
  const top3Specialties = (specialtiesList.length > 3
    ? specialtiesList.slice(0, 3)
    : specialtiesList
  ).join(', ');
  const modal = useRef(null);
  const [buttonStatus, setButtonStatus] = useState('');
  const [relation, setRelation] = useState({ status: '' });
  const [color, setColor] = useState();
  const [mostActiveGroups, setMostActiveGroups] = useState([]);
  const afterAddOrAccept = () => {
    switch (buttonStatus) {
      case ALL_STATUS.ACCEPT:
        setButtonStatus(ALL_STATUS.REQUEST_ACCEPTED);
        break;
      case ALL_STATUS.ADD_FRIEND:
        setButtonStatus(ALL_STATUS.REQUEST_SENT);
        break;
      default:
        break;
    }
  };
  const report = () => {
    modal.current = Modal.info({
      maskClosable: true,
      width: '80%',
      icon: '',
      closeIcon: <CloseIcon />,
      closable: true,
      content: (
        <ReportModalContent
          {...reportProps}
          closeModal={() => modal.current.destroy()}
        />
      ),
      centered: true,
      className: 'dscomponent-report-modal',
      okText: '',
    });
  };
  // fetch status
  useEffect(async () => {
    const { data } = await DataProvider.invoke('user', 'getRelations', {
      userId,
      friends: [friendId],
    });
    if (data[friendId]) {
      setRelation({ ...relation, ...data[friendId] });
      const { status } = data[friendId];
      setButtonStatus(RELATION_STATUS[status]);
    } else {
      buttonStatus === ALL_STATUS.ADD_FRIEND ||
        setButtonStatus(ALL_STATUS.ADD_FRIEND);
    }
  }, [userId, friendId]);

  useEffect(() => {
    async function getMostActiveGroup() {
      const { data: groups } = await DataProvider.invoke(
        'user',
        'getMostActiveGroups',
        {
          userId: friendId,
          canViewCGA,
        }
      );
      setMostActiveGroups(groups);
    }
    if (friendId) {
      getMostActiveGroup();
    }
  }, [canViewCGA, friendId]);

  const getColorIndex = colorIndex => {
    setColor(theme.avatar[`avatar${colorIndex}`]);
  };
  const onAddFriendClicked = () => {
    if ([ALL_STATUS.ACCEPT, ALL_STATUS.ADD_FRIEND].includes(buttonStatus)) {
      onAcceptOrAddClick({
        relationStatus: relation.status,
        buttonStatus,
        relationId: relation.id,
        userId,
        friendId,
        callback: afterAddOrAccept,
      });
    } else if (buttonStatus && buttonStatus === ALL_STATUS.REQUEST_ACCEPTED) {
      onSendMessage({ relation, setRelation });
    } else {
      return;
    }
  };
  return NEW_COMMUNITY_PROFILE ? (
    <NewPopOverlay
      location={getDisplayValue(
        `${city || ''}${country && city ? ' ,' : ''}${country || ''}`
      )}
      bio={description}
      userId={friendId}
      avatarName={avatarName}
      avatarSrc={avatarSrc}
      name={userName}
      role={Roles_Display[role]}
      onAddFriendClicked={onAddFriendClicked}
      requestAccepted={buttonStatus === ALL_STATUS.REQUEST_ACCEPTED}
      tooltipContent={buttonStatus}
      addFriendDisabled={
        ![
          ALL_STATUS.ACCEPT,
          ALL_STATUS.ADD_FRIEND,
          ALL_STATUS.REQUEST_ACCEPTED,
        ].includes(buttonStatus)
      }
    />
  ) : (
    <HoverCardContainer className={`${prefixCls}-card-container`}>
      <Header>
        <BioBackground imgSrc={avatarSrc} backColor={color} />
        <Tooltip
          destroyTooltipOnHide
          arrowPointAtCenter
          placement="bottom"
          title={
            <ReportTooltipTitle onClick={report}>Report</ReportTooltipTitle>
          }
        >
          <MoreOutlined
            rotate={90}
            color="#73747d"
            style={{
              position: 'absolute',
              right: '24px',
              top: '24px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          />
        </Tooltip>
        <Avatar
          getColorIndex={getColorIndex}
          type={AVATAR_TYPE.USER}
          src={avatarSrc}
          name={avatarName}
          style={{ width: '70px', height: '70px' }}
          size={70}
        />
        <StyledUserName className={`${prefixCls}-card-name`}>
          {userName}
          {pronoun ? ` (${pronoun})` : ''}
        </StyledUserName>
      </Header>
      <BioContainer>
        <BioLinesContainer>
          {role && (
            <BioLine>
              <AccountBoxIcon style={bioIconStyle} />
              {Roles_Display[role]}
            </BioLine>
          )}
          {university && (
            <BioLine>
              <AccountBalanceIcon style={bioIconStyle} />
              {university}
            </BioLine>
          )}
          {city && country && (
            <BioLine>
              <LocationOnIcon style={bioIconStyle} />
              {city},{` ${country}`}
            </BioLine>
          )}
        </BioLinesContainer>
        {!buttonStatus && <Skeleton.Input style={{ width: '255px' }} active />}
        {[ALL_STATUS.ACCEPT, ALL_STATUS.ADD_FRIEND].includes(buttonStatus) && (
          <StyledButtonOutlined
            width="100%"
            className={relation.status === 'BLOCK' ? 'block-add' : ''}
            onClick={() => onAddFriendClicked()}
          >
            {buttonStatus}
          </StyledButtonOutlined>
        )}
        {buttonStatus && buttonStatus === ALL_STATUS.REQUEST_SENT && (
          <StyledButtonDisabled width="100%">
            {buttonStatus}
          </StyledButtonDisabled>
        )}
        {buttonStatus && buttonStatus === ALL_STATUS.REQUEST_ACCEPTED && (
          <StyledButtonFilled width="100%" onClick={() => onAddFriendClicked()}>
            {buttonStatus}
          </StyledButtonFilled>
        )}
        <Descriptions>
          {!isExpert && mostActiveGroups.length > 0 && (
            <DescriptionLine>
              Most Active Group{mostActiveGroups.length !== 1 ? 's' : ''}
              <ActiveGroups>
                {mostActiveGroups.map((item, index) => (
                  <Link to={`/${item.slug}?about`} key={item.id}>
                    {item.name}
                    {index !== mostActiveGroups.length - 1 ? `, ` : ''}
                  </Link>
                ))}
              </ActiveGroups>
            </DescriptionLine>
          )}
          {!isExpert && specialties && (
            <DescriptionLine style={{ marginTop: '20px' }}>
              Specialties
              <DescriptionLineContent>{top3Specialties}</DescriptionLineContent>
            </DescriptionLine>
          )}
          {isExpert && description && (
            <DescriptionLine style={{ marginTop: '0px' }}>
              About
              <DescriptionLineContent>{description}</DescriptionLineContent>
            </DescriptionLine>
          )}
        </Descriptions>
      </BioContainer>
    </HoverCardContainer>
  );
};

/**
 * @param {string} avatarSrc [required] -card user avatar src
 * @param {string} avatarName [required] -card user avatar display name
 * @param {string} userName [required] -card user full name
 * @param {string} cardUserId [required] -card user id
 * @param {string} currentUserId [required] -current user id
 * @param {object} bio [required] -object for popover card bio infomation
 * @param {boolean} isExpert [required] -Is popover user an expert
 * @param {boolean} canViewCGA [required] -can current user view CGA group
 * @param {string} prefixCls [optinal] -prefix class
 * @param {string} rest [optinal] -antd PopOver props:https://ant.design/components/popover-cn/#API
 */
const FriendCardPopOver = props => {
  const {
    prefixCls,
    avatarSrc,
    avatarName,
    userName,
    cardUserId: friendId,
    currentUserId: userId,
    canViewCGA,
    isExpert,
    reportProps,
    bio,
    ...rest
  } = { arrowPointAtCenter: true, isExpert: false, ...props };
  return (
    <div
      onClick={e => {
        e.preventDefault();
      }}
      style={{ cursor: friendId !== userId ? 'pointer' : 'unset' }}
    >
      {friendId !== userId && (
        <Popover
          arrowPointAtCenters
          overlayClassName="Cr_Cm_Friend_Card_Popover_Overlay"
          trigger={isApp ? 'click' : 'hover'}
          getPopupContainer={trigger => trigger?.parentElement}
          content={
            <PopOverlay
              {...{
                reportProps,
                isExpert,
                canViewCGA,
                bio,
                prefixCls,
                avatarSrc,
                avatarName,
                userName,
                userId,
                friendId,
              }}
            />
          }
          {...rest}
        />
      )}
      {/* if current user hover on himself just show the component being hovered */}
      {friendId === userId && rest.children}
    </div>
  );
};

export default FriendCardPopOver;
